/**
 * صفحة الحي.
 * Hero = ٤ KPIs فقط ثم المخطط ثم ضغط السوق ثم «تفاصيل السوق» ثم آخر الصفقات.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { color } from '@/design/tokens';
import { Button, Card, KpiCell, SectionTitle, SegmentedControl } from '@/components/primitives/Primitives';
import { PriceChart } from '@/components/data/Charts';
import { DealsTable, MarketPressureMeter, MetricCard } from '@/components/data/Market';
import { ErrorState, LoadingState, NoDataState } from '@/components/states/States';
import { UnavailablePanel } from '@/components/states/Unavailable';
import { useDistrict, useWatchlist } from '@/services/market';
import { useToast } from '@/app/AppShell';
import { numOrDash, pctOrDash, perSqmOrDash, scoreOrDash } from '@/lib/format';
import type { ChartRange } from '@/domain/types';

const RANGES: { value: ChartRange; label: string }[] = [
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '6M', label: '6M' },
  { value: '1Y', label: '1Y' },
  { value: '3Y', label: '3Y' },
];

const RANGE_POINTS: Record<ChartRange, number> = { '1M': 2, '3M': 4, '6M': 7, '1Y': 13, '3Y': 36 };

export default function DistrictRoute(): JSX.Element {
  const { districtId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const decoded = districtId ? decodeURIComponent(districtId) : undefined;
  const state = useDistrict(decoded);
  const watchlist = useWatchlist();
  const [range, setRange] = useState<ChartRange>('1Y');

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div style={{ paddingTop: 36 }}>
        <LoadingState message="نقرأ صفقات ٩٠ يوماً..." />
      </div>
    );
  }
  if (state.status === 'unavailable') {
    return (
      <div style={{ paddingTop: 36 }}>
        <UnavailablePanel reason="coming-soon" title="صفحة الحي" />
      </div>
    );
  }
  if (state.status === 'no-data') {
    return (
      <div style={{ paddingTop: 36 }}>
        <NoDataState onExplore={() => navigate('/map')} />
      </div>
    );
  }
  if (state.status === 'error' || !state.data) {
    return (
      <div style={{ paddingTop: 36 }}>
        <ErrorState onRetry={state.reload} onEdit={() => navigate('/map')} />
      </div>
    );
  }

  const d = state.data;
  const series = d.series.slice(-RANGE_POINTS[range]);
  const watched = watchlist.isWatched(d.id);

  return (
    <div className="nq-enter" style={{ paddingTop: 36, display: 'grid', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.02em' }}>{d.name}</h1>
          <p style={{ marginTop: 6, fontSize: 13, color: color.textSubtle }}>
            {d.city} · {d.zone}
          </p>
        </div>
        <Button
          variant={watched ? 'ghost' : 'primary'}
          onClick={() => {
            if (watched) {
              void watchlist.remove(d.id);
              toast('أزلنا الحي من المراقبة');
            } else {
              void watchlist.add(d.id);
              toast('أضفنا الحي إلى المراقبة');
            }
          }}
        >
          {watched ? 'إزالة من المراقبة' : 'راقب الحي'}
        </Button>
      </div>

      {/* Hero — 4 KPIs فقط */}
      <div className="nq-grid-numbers" data-testid="district-hero">
        <KpiCell label="متوسط سعر التنفيذ" value={numOrDash(d.dealPricePerSqm)} unit="ر.س/م²" />
        <KpiCell label="متوسط سعر العرض" value={numOrDash(d.askPricePerSqm)} unit="ر.س/م²" tone="warn" />
        <KpiCell label="فجوة العرض/التنفيذ" value={pctOrDash(d.askDealGapPct)} />
        <KpiCell label="مؤشر الفرصة" value={scoreOrDash(d.opportunityScore)} unit="من 100" tone="accent" />
      </div>

      <Card as="section">
        <SectionTitle
          title="سعر التنفيذ مقابل سعر العرض"
          action={
            <SegmentedControl options={RANGES} value={range} onChange={setRange} ariaLabel="المدة الزمنية" />
          }
        />
        {series.length > 1 ? (
          <PriceChart series={series} />
        ) : (
          <UnavailablePanel
            reason="insufficient-data"
            title="مخطط السعر"
            detail="نحتاج نقاطاً شهرية كافية من الصفقات المسجّلة لرسم مسار السعر."
          />
        )}
      </Card>

      <Card as="section">
        <SectionTitle title="ضغط السوق" />
        {d.pressure ? (
          <MarketPressureMeter pressure={d.pressure} />
        ) : (
          <UnavailablePanel reason="coming-soon" title="قوة المشتري" />
        )}
      </Card>

      <section>
        <SectionTitle title="تفاصيل السوق" />
        <div className="nq-row">
          <MetricCard
            label="عدد الصفقات"
            value={numOrDash(d.details.deals90d)}
            context="صفقة مسجّلة في المدة المرجعية"
          />
          <MetricCard label="حجم المعروض" value={numOrDash(d.details.supply)} context="يحتاج مصدر إعلانات" />
          <MetricCard
            label="متوسط مدة الإعلان"
            value={numOrDash(d.details.avgDaysOnMarket)}
            context="يحتاج مصدر إعلانات"
          />
          <MetricCard
            label="نسبة تخفيضات الأسعار"
            value={pctOrDash(d.details.priceCutRate)}
            context="يحتاج مصدر إعلانات"
            tone="warn"
          />
          <MetricCard
            label="السيولة"
            value={scoreOrDash(d.details.liquidityScore)}
            context="مؤشر 0–100"
          />
          <MetricCard
            label="حركة السعر"
            value={pctOrDash(d.details.annualGrowth)}
            context="بين آخر فترتين متتاليتين"
            tone={(d.details.annualGrowth ?? 0) >= 0 ? 'up' : 'down'}
          />
        </div>
      </section>

      <Card as="section">
        <SectionTitle title="آخر الصفقات المسجلة" />
        <DealsTable deals={d.recentDeals} />
        <p style={{ marginTop: 14, fontSize: 10.5, color: color.micro }}>
          {/* رموز الإصدارات الداخلية لا تظهر للمستخدم — تبقى في lineage. */}
          نطاق القيمة العادلة للمتر في هذا الحي:{' '}
          <span className="nq-num" dir="ltr">
            {perSqmOrDash(d.fairValueLowPerSqm)} – {perSqmOrDash(d.fairValueHighPerSqm)}
          </span>
        </p>
      </Card>
    </div>
  );
}
